// Regole delle pagine Immobili del cliente (bozza design/immobili-v3.html): filtri, ordinamenti, €/m²,
// "da decidere", pallino dell'agenzia, confronto, dintorni, storia, stima della rata.
// €/m² e "da decidere" arrivano dal server (prezzo_mq, da_decidere, stessa regola provata in tests/test_immobili.py);
// se mancano (backend non ancora aggiornato) si calcolano qui allo stesso modo.
import {
  etichettaImmobile, leggiData, leggiDataUtc, leggiDataUtcRoma, visitaInSospeso, formatEuro, metriQuadri, quandoGiorno, ora,
  giornoEMese, dataLunga, dominio, daCompilare, titoloImmobile, MOTIVI, MOTIVI_SOLO_STAFF, STATI_PROPOSTA_CHIUSI, migliaia, propostaNonAccettata,
  ilGiorno,
} from './percorso';
import { conAnno, testoEsitoCliente } from './visite';
import { contesto, dataIn, PRINCIPALE } from '../i18n';

export const STATI_PRIMA_DELLA_VISITA = ['nuovo', 'da_contattare', 'non_risponde', 'richiamare', 'da_visionare'];

// La lingua del cliente è l'ultimo parametro delle funzioni che scrivono testo (di base l'italiano: la scheda dello
// staff, che non la passa, resta com'era). Le date qui sono già nell'ora di Roma: niente timeZone.
const scritta = (lingua, d, opzioni) => dataIn(lingua, d, { ...opzioni, timeZone: undefined });

// Fasce orarie della richiesta di visita e motivi di "Non ci interessa" (chiavi uguali al server)
export const PREFERENZE_VISITA = [
  ['mattina', 'Mattina'], ['pomeriggio', 'Pomeriggio'], ['sera', 'Sera'], ['sabato', 'Sabato'], ['quando_volete', 'Quando volete'],
];
export const MOTIVI_NON_INTERESSA = [
  ['prezzo_alto', 'Prezzo'], ['zona_non_adatta', 'Zona'], ['metratura_inadeguata', 'Metratura'],
  ['piano_non_adatto', 'Piano'], ['condizioni_immobile', 'Stato'], ['altro', 'Altro'],
];
// Le stesse voci nella lingua del cliente: [chiave, etichetta]
export const preferenzeVisita = (lingua = PRINCIPALE) => PREFERENZE_VISITA
  .map(([chiave]) => [chiave, contesto(lingua).t(`immobili.preferenze.${chiave}`)]);
export const motiviNonInteressa = (lingua = PRINCIPALE) => MOTIVI_NON_INTERESSA
  .map(([chiave]) => [chiave, contesto(lingua).t(`immobili.motivi_scarto_scelta.${chiave}`)]);
// I motivi come li vede il CLIENTE: senza i due «non si è presentato», e con la riserva italiana se la
// traduzione manca o torna vuota. L'elenco non è mai vuoto: è costruito su una costante, non su una chiamata.
export const motiviVisita = (lingua = PRINCIPALE) => MOTIVI
  .filter(([chiave]) => !MOTIVI_SOLO_STAFF.includes(chiave))
  .map(([chiave, riserva]) => {
    const tradotto = contesto(lingua).t(`immobili.motivi.${chiave}`);
    return [chiave, tradotto && tradotto !== `immobili.motivi.${chiave}` ? tradotto : riserva];
  });
const ETICHETTE_MOTIVI_VISITA = Object.fromEntries(MOTIVI);

// ---------------------------------------------------------------- numeri

const numeroMq = (superficie) => {
  const testo = String(superficie ?? '');
  const migliaia = testo.match(/\d{1,3}(?:\.\d{3})+/);
  if (migliaia) return parseFloat(migliaia[0].replace(/\./g, ''));
  const semplice = testo.match(/\d+(?:[.,]\d+)?/);
  const n = semplice ? parseFloat(semplice[0].replace(',', '.')) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
};

// Sotto i 5.000 € un prezzo non è plausibile (1 €, 0, un dato letto male): come il server (CAMPI_NUMERO di
// dati_annuncio.py e la stima della rata) non vale come prezzo, né per l'ordinamento né per il confronto
export const PREZZO_MINIMO = 5000;

const prezzoGrezzo = (immobile) => {
  if (typeof immobile.prezzo === 'number' && immobile.prezzo > 0) return immobile.prezzo;
  const n = parseFloat(String(immobile.prezzo_testo ?? '').replace(/[^\d,]/g, '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const prezzoNumero = (immobile) => {
  const n = prezzoGrezzo(immobile);
  return n && n >= PREZZO_MINIMO ? n : null;
};

// C'è un prezzo ma non è plausibile (es. 1 €): "Prezzo da verificare" invece di "—"
export const prezzoDaVerificare = (immobile) => !!prezzoGrezzo(immobile) && !prezzoNumero(immobile);

// Il prezzo a parole, uguale nella card dell'elenco, nella scheda e nella card "I vostri immobili" della home (che
// prima scriveva "1 €"): "298.000 €", "Prezzo da verificare" (non plausibile), "Prezzo in arrivo" (dati dell'annuncio
// non ancora arrivati), "Prezzo non indicato"
export const testoPrezzo = (immobile, lingua = PRINCIPALE) => {
  const n = prezzoNumero(immobile);
  if (n) return formatEuro(n, lingua);
  const { t } = contesto(lingua);
  if (prezzoDaVerificare(immobile)) return t('immobili.prezzi.da_verificare');
  return t(daCompilare(immobile) ? 'immobili.prezzi.in_arrivo' : 'immobili.prezzi.non_indicato');
};

// Stessa regola del server (prezzo_al_mq): niente se manca il prezzo o la superficie è sotto i 10 m²
export const prezzoMq = (immobile) => {
  if (immobile.prezzo_mq) return immobile.prezzo_mq;
  const prezzo = prezzoNumero(immobile);
  const mq = numeroMq(immobile.superficie);
  return prezzo && mq && mq >= 10 ? Math.round(prezzo / mq) : null;
};

export const formatEuroMq = (valore, lingua = PRINCIPALE) => (valore
  ? contesto(lingua).t('immobili.euro_mq', { valore: lingua === PRINCIPALE ? migliaia(valore) : contesto(lingua).numero(Math.round(valore)) }) : null);

// Prezzo sul segnaposto della mappa: "229k €", "1,25 mln €"; "—" se manca o non è plausibile (mai "0k €")
export const prezzoSullaMappa = (prezzo) => {
  if (!prezzo || !(prezzo >= PREZZO_MINIMO)) return '—';
  return prezzo >= 1000000 ? `${(prezzo / 1000000).toLocaleString('it-IT', { maximumFractionDigits: 2 })} mln €` : `${Math.round(prezzo / 1000)}k €`;
};

export const pianoBreve = (piano) => {
  const testo = String(piano ?? '').trim();
  if (!testo) return null;
  return /^\d+$/.test(testo) ? `${testo}°` : testo;
};

// Il piano come si dice: "3°" + "piano" per un numero; per una parola una frase sola ("Piano terra", "Piano
// rialzato", "Ultimo piano", "Seminterrato"). Prima il badge era valore + "piano": "terra piano".
const PIANI_A_PAROLE = { terra: 'piano_terra', t: 'piano_terra', pt: 'piano_terra', rialzato: 'piano_rialzato', r: 'piano_rialzato',
  ammezzato: 'piano_ammezzato', ultimo: 'ultimo_piano', attico: 'ultimo_piano' };
export const pianoDato = (piano, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const breve = pianoBreve(piano);
  if (!breve) return null;
  if (/^\d+°$/.test(breve)) {
    return { valore: lingua === PRINCIPALE ? breve : t('immobili.dati.piano_numero', { n: breve.replace('°', '') }), etichetta: t('immobili.dati.piano') };
  }
  const parola = breve.toLowerCase().replace(/^piano\s+/, '').replace(/\s+piano$/, '').trim();
  const nota = PIANI_A_PAROLE[parola];
  return { valore: nota ? t(`immobili.dati.${nota}`) : conMaiuscola(/piano/i.test(breve) ? breve.toLowerCase() : breve), etichetta: '' };
};

// ---------------------------------------------------------------- chi l'ha inserito, decisione

export const inseritoDalloStaff = (immobile) => ['admin', 'collaboratore'].includes(immobile.inserito_da_tipo);

export const daDecidere = (immobile, visite) => {
  if (typeof immobile.da_decidere === 'boolean') return immobile.da_decidere;
  return inseritoDalloStaff(immobile)
    && !visite.some((v) => v.immobile_id === immobile.id)
    && !immobile.visita_richiesta
    && STATI_PRIMA_DELLA_VISITA.includes(immobile.stato_attivita || 'nuovo')
    && !daCompilare(immobile);
};

// Pallino dell'agenzia: quello che conta è la **dichiarazione** — la conferma formale che l'agenzia lavora con
// noi — non il giudizio che ci siamo fatti noi sulla collaborazione (backend/server.py, stato_agenzia).
// I nomi vecchi (collabora / non_collabora) arrivano ancora da qualche riga di prima: si portano sui nuovi.
const VECCHI_NUOVI = { collabora: 'in_attesa', non_collabora: 'non_dichiara', lavorabile: 'in_attesa', non_lavorabile: 'non_dichiara' };
export const statoAgenzia = (immobile) => {
  const detto = immobile.agenzia_info?.stato;
  if (detto) return VECCHI_NUOVI[detto] || detto;
  if (!immobile.agenzia && !immobile.agenzia_id) return null;
  return VECCHI_NUOVI[immobile.stato_lavorabilita] || 'in_attesa';
};
// Per il cliente: «dichiarata» vuol dire che quell'agenzia ha confermato di lavorare con noi
export const TESTI_AGENZIA = { dichiarata: 'Dichiarata', non_dichiara: 'Non ha dichiarato', in_attesa: 'Dichiarazione da confermare' };
export const testoAgenzia = (stato, lingua = PRINCIPALE) => (TESTI_AGENZIA[stato] ? contesto(lingua).t(`immobili.agenzia.${stato}`) : null);

// "Aggiunto da voi il 5 set" / "Proposto da Karolina il 10 set" / "Aggiunto oggi 14:02". Nell'ora di Roma (adesso =
// adessoRoma): prima l'ora del telefono, "oggi 03:00" a New York per una casa aggiunta alle 9 di Roma
export const provenienza = (immobile, adesso, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const d = leggiDataUtcRoma(immobile.data_aggiunta);
  const giorno = d ? quandoGiorno(d, adesso, lingua) : '';
  // una data di un altro anno si scrive con l'anno (conAnno): prima "Aggiunto da voi il 17 agosto" anche per il 2025
  let quando = '';
  if (d && giorno === t('calendario.oggi')) quando = t('immobili.provenienza.oggi', { ora: ora(d) });
  else if (d && giorno === t('calendario.ieri')) quando = giorno;
  else if (d) quando = lingua === PRINCIPALE ? conAnno(ilGiorno(giornoEMese(d)), d, adesso) : scritta(lingua, d, { day: 'numeric', month: 'long' });
  if (inseritoDalloStaff(immobile)) {
    const nome = immobile.inserito_da_nome || t('immobili.provenienza.noi');
    return t(quando ? 'immobili.provenienza.nostra' : 'immobili.provenienza.nostra_senza', { nome, quando });
  }
  return t(quando ? 'immobili.provenienza.vostra' : 'immobili.provenienza.vostra_senza', { quando });
};

// ---------------------------------------------------------------- filtri e ordinamento

// Una regola sola per "tolta dalla ricerca": l'etichetta grigia (scartata da voi o dallo staff, "Non fa per voi"
// dopo la visita, "Proposta non accettata"). La usano il filtro "Scartati", la card e la scheda (niente prenotazioni
// né "Non ci interessa più"): prima la scheda guardava solo lo stato "scartato" e dopo "Non fa per noi" offriva
// ancora "Prenotate un'altra visita"
export const fuoriRicerca = (etichetta) => etichetta?.classe === 'scartato';

// Una casa che non c'è più non è una casa «scartata». Per il cliente «Scartati» vuol dire «quelle che ho detto
// di no io»: trovarci dentro una casa che gli piaceva, venduta da un altro, è un dispiacere e una bugia insieme.
// Vanno in una sezione loro, in fondo (decisione dell'utente, 03/10/2026).
export const nonPiuDisponibile = (immobile) => Boolean(
  immobile?.annuncio_non_online || ['venduto', 'affittato'].includes((immobile?.venduto || {}).stato));

// «Venduto · rilevato il 04/10/2026», «Affittato il 12/09/2026», «Non più online dal 28/09/2026»
export const testoNonDisponibile = (immobile, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const voce = immobile?.venduto || {};
  if (['venduto', 'affittato'].includes(voce.stato)) {
    const quando = dataIn(lingua, leggiDataUtc(voce.data));
    const chiave = voce.data_rilevata || voce.data_nel_futuro ? 'rilevato' : 'il';
    return t(`immobili.non_disponibile.${voce.stato}_${chiave}`, { data: quando });
  }
  const dal = immobile?.non_online_dal ? dataIn(lingua, leggiDataUtc(immobile.non_online_dal)) : null;
  return dal ? t('immobili.non_disponibile.non_online_dal', { data: dal }) : t('immobili.non_disponibile.non_online');
};

const inGioco = (e) => !fuoriRicerca(e.etichetta) && !nonPiuDisponibile(e.immobile);

export const FILTRI = [
  { chiave: 'attivi', nome: 'Attivi', prende: inGioco },
  { chiave: 'da_decidere', nome: 'Da decidere', prende: (e) => e.daDecidere },
  // una casa ancora da decidere ("Proposto da Karolina") non è da visitare finché non dite sì
  // (né una con l'annuncio tolto dal portale, finché l'agenzia non conferma); nemmeno una già visitata che aspetta
  // il vostro parere ("Com'è andata?": la visita c'è stata)
  { chiave: 'da_visitare', nome: 'Da visitare', prende: (e) => inGioco(e) && !e.daDecidere && !e.visitata && (e.etichetta.classe === 'visita' || e.etichetta.testo === 'Visita richiesta'
    || (e.immobile.stato_attivita === 'da_visionare' && !e.immobile.annuncio_non_online)) },
  { chiave: 'trattativa', nome: 'In trattativa', prende: (e) => e.etichetta.classe === 'proposta' },
  { chiave: 'proposti', nome: 'Proposti da noi', prende: (e) => inGioco(e) && inseritoDalloStaff(e.immobile) },
  // «Scartati» resta quello che il cliente (o lo staff) ha detto di no: le case che non ci sono più hanno la loro
  { chiave: 'scartati', nome: 'Scartati', prende: (e) => fuoriRicerca(e.etichetta) && !nonPiuDisponibile(e.immobile) },
  { chiave: 'non_disponibili', nome: 'Non più disponibili', prende: (e) => nonPiuDisponibile(e.immobile) },
];

// Di base l'ordine di inserimento (il più vecchio in alto, il più recente in fondo); prezzo e €/m² solo se scelti
export const ORDINAMENTI = [
  { chiave: 'inserimento', nome: 'come inseriti' },
  { chiave: 'prezzo', nome: 'prezzo' },
  { chiave: 'mq', nome: '€/m²' },
];
// I nomi di filtri e ordinamenti nella lingua del cliente
export const nomeFiltro = (chiave, lingua = PRINCIPALE) => contesto(lingua).t(`immobili.filtri.${chiave}`);
export const nomeOrdinamento = (chiave, lingua = PRINCIPALE) => contesto(lingua).t(`immobili.ordinamenti.${chiave}`);

// Ordine di inserimento stabile: data di aggiunta crescente, a parità (o senza data) resta l'ordine ricevuto.
// Lo stato o una modifica non spostano mai una casa.
export const perInserimento = (elenco, data = (x) => x.aggiunto) => elenco
  .map((x, n) => ({ x, n, t: data(x) }))
  .sort((a, b) => ((a.t ?? -Infinity) - (b.t ?? -Infinity)) || a.n - b.n)
  .map(({ x }) => x);

// Etichetta per il cliente: quella di etichettaImmobile (che con l'annuncio tolto dal portale dice "Annuncio non più
// online", in home come qui), con "da decidere" calcolato qui se il server non lo dà
export const etichettaCliente = (immobile, visite, proposte, adesso, lingua = PRINCIPALE) => etichettaImmobile(
  { ...immobile, da_decidere: daDecidere(immobile, visite) }, visite, proposte, adesso, lingua,
);

// Visitata e in attesa del vostro parere: una visita passata senza esito (non annullata, senza uno spostamento
// chiesto e ancora aperto) e nessuna visita in programma. Non è più "da visitare".
export const visitataSenzaEsito = (immobile, visite, adesso) => {
  const sue = visite.filter((v) => v.immobile_id === immobile.id && !v.annullata && !v.esito_visita && leggiData(v.data_visita));
  if (sue.some((v) => leggiData(v.data_visita) > adesso)) return false;
  return sue.some((v) => !v.spostamento_richiesto);
};

// "Volete vederla?" solo se l'annuncio è ancora online: prima l'agenzia deve dirci se la casa è in vendita
export const daDecidereOra = (immobile, visite) => daDecidere(immobile, visite) && !immobile.annuncio_non_online;

// Ogni immobile con etichetta, "da decidere" e numeri già pronti
export const preparaElenco = (immobili, visite, proposte, adesso, lingua = PRINCIPALE) => immobili.map((immobile) => ({
  immobile,
  etichetta: etichettaCliente(immobile, visite, proposte, adesso, lingua),
  daDecidere: daDecidereOra(immobile, visite),
  visitata: visitataSenzaEsito(immobile, visite, adesso),
  prezzo: prezzoNumero(immobile),
  prezzoMq: prezzoMq(immobile),
  aggiunto: leggiDataUtc(immobile.data_aggiunta),
}));

export const conteggi = (elenco) => Object.fromEntries(FILTRI.map((f) => [f.chiave, elenco.filter(f.prende).length]));

export const filtraEOrdina = (elenco, filtro, ordinamento) => {
  const f = FILTRI.find((x) => x.chiave === filtro) || FILTRI[0];
  const scelti = perInserimento(elenco).filter(f.prende);
  const chiave = { prezzo: (e) => e.prezzo, mq: (e) => e.prezzoMq }[ordinamento];
  if (!chiave) return scelti;
  // a parità di prezzo resta l'ordine di inserimento; senza prezzo in fondo
  const valore = (e) => (chiave(e) == null ? Infinity : chiave(e));
  return scelti.map((e, n) => ({ e, n })).sort((a, b) => (valore(a.e) - valore(b.e)) || a.n - b.n).map(({ e }) => e);
};

// "7 case, 3 da decidere" (le case scartate non contano)
export const titoloElenco = (elenco, lingua = PRINCIPALE) => {
  const { tp } = contesto(lingua);
  const case_ = elenco.filter(inGioco).length;
  const decidere = elenco.filter((e) => e.daDecidere).length;
  return { case: tp('immobili.plurali.case', case_), decidere: decidere ? tp('immobili.plurali.da_decidere', decidere) : null };
};

// ---------------------------------------------------------------- dintorni

// icona = nome di un'icona a linea di components/cliente/Icone.jsx (niente emoji); breve = per il confronto
export const DINTORNI = {
  metro: { icona: 'metro', nome: 'Metropolitana', breve: 'metro' },
  stazione: { icona: 'treno', nome: 'Stazione', breve: 'treno' },
  bus: { icona: 'bus', nome: 'Fermata del bus', breve: 'bus' },
  supermercato: { icona: 'carrello', nome: 'Supermercato', breve: 'spesa' },
  farmacia: { icona: 'farmacia', nome: 'Farmacia', breve: 'farmacia' },
  scuola: { icona: 'scuola', nome: 'Scuola primaria', breve: 'scuola' },
  parco: { icona: 'albero', nome: 'Parco', breve: 'parco' },
};
const ORDINE_DINTORNI = ['metro', 'stazione', 'bus', 'supermercato', 'farmacia', 'scuola', 'parco'];
const ORDINE_PILLOLE = ['metro', 'stazione', 'bus', 'supermercato', 'scuola', 'parco', 'farmacia'];

// Il nome della categoria nella lingua del cliente ("Metropolitana", "Fermata del bus") e la sua forma breve
const nomeDintorno = (c, lingua) => contesto(lingua).t(`immobili.dintorni.${c}`);
const breveDintorno = (c, lingua) => contesto(lingua).t(`immobili.dintorni.brevi.${c}`);

export const vociDintorni = (immobile, lingua = PRINCIPALE) => {
  const categorie = immobile.dintorni?.categorie || {};
  return ORDINE_DINTORNI.filter((c) => categorie[c])
    // nome = quello del luogo dal server (la fermata, il supermercato); se manca, la categoria nella lingua
    .map((c) => ({ chiave: c, ...DINTORNI[c], ...categorie[c], titolo: nomeDintorno(c, lingua), nome: categorie[c].nome || nomeDintorno(c, lingua) }));
};

// Pillole della card: al massimo 4; il treno o la metro con il nome della stazione
export const pilloleDintorni = (immobile, quante = 4, lingua = PRINCIPALE) => {
  const categorie = immobile.dintorni?.categorie || {};
  const scelte = ORDINE_PILLOLE.filter((c) => categorie[c]);
  // metro e stazione insieme sono troppe: resta la più vicina
  if (scelte.includes('metro') && scelte.includes('stazione')) {
    scelte.splice(scelte.indexOf(categorie.metro.minuti <= categorie.stazione.minuti ? 'stazione' : 'metro'), 1);
  }
  return scelte.slice(0, quante).map((c) => ({
    chiave: c, icona: DINTORNI[c].icona, minuti: categorie[c].minuti, breve: breveDintorno(c, lingua), titolo: nomeDintorno(c, lingua),
    nome: ['metro', 'stazione'].includes(c) ? categorie[c].nome : null,
  }));
};

// ---------------------------------------------------------------- dati della casa (DatoImmobile)

const conMaiuscola = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);

// Ogni dato con la sua icona a linea: m², locali, bagni, piano, ascensore, classe, spese, anno, stato,
// riscaldamento. Un dato che manca non c'è (niente "—").
export const datiImmobile = (immobile, lingua = PRINCIPALE) => {
  const { t, tp } = contesto(lingua);
  const mq = metriQuadri(immobile.superficie);
  const bagni = immobile.bagni != null && String(immobile.bagni).trim();
  const riscaldamento = immobile.riscaldamento && immobile.riscaldamento.replace(/^riscaldamento\s*/i, '').trim();
  const piano = pianoDato(immobile.piano, lingua);
  return [
    mq && { chiave: 'superficie', icona: 'metri', valore: mq.replace(' m²', ''), etichetta: t('immobili.dati.mq') },
    // l'etichetta è la parola al plurale giusto per quel numero (in russo e ucraino "3 комнаты", "5 комнат")
    immobile.locali && { chiave: 'locali', icona: 'porta', valore: String(immobile.locali), etichetta: tp('immobili.plurali.locali_parola', Math.floor(Number(immobile.locali)) || 1) },
    bagni && { chiave: 'bagni', icona: 'bagno', valore: bagni, etichetta: tp('immobili.plurali.bagni_parola', Math.floor(Number(bagni)) || 1) },
    piano && { chiave: 'piano', icona: 'scale', ...piano },
    immobile.ascensore != null && { chiave: 'ascensore', icona: 'ascensore', valore: t(immobile.ascensore ? 'immobili.dati.con_ascensore' : 'immobili.dati.senza_ascensore'), etichetta: t('immobili.dati.ascensore') },
    immobile.classe_energetica && { chiave: 'classe', icona: 'foglia', valore: immobile.classe_energetica, etichetta: t('immobili.dati.classe'), prima: true },
    immobile.spese_mensili && { chiave: 'spese', icona: 'euro', valore: formatEuro(immobile.spese_mensili, lingua), etichetta: t('immobili.dati.spese') },
    immobile.anno_costruzione && { chiave: 'anno', icona: 'calendario', valore: String(immobile.anno_costruzione), etichetta: t('immobili.dati.anno'), prima: true },
    immobile.condizioni && { chiave: 'stato', icona: 'chiave', valore: conMaiuscola(immobile.condizioni), etichetta: '' },
    riscaldamento && { chiave: 'riscaldamento', icona: 'fiamma', valore: conMaiuscola(riscaldamento), etichetta: '' },
  ].filter(Boolean);
};

// ---------------------------------------------------------------- confronto

// chiave = la voce nel dizionario (immobili.confronto.*); nome = come si legge, nella lingua del cliente
export const righeConfronto = (a, b, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const riga = (chiave, valore, icona = null) => ({ chiave, nome: t(`immobili.confronto.${chiave}`), icona, valori: [valore(a), valore(b)] });
  const agenzia = (e) => (e.immobile.agenzia_info?.nome || e.immobile.agenzia
    ? `${e.immobile.agenzia_info?.nome || e.immobile.agenzia}${statoAgenzia(e.immobile) ? ` · ${testoAgenzia(statoAgenzia(e.immobile), lingua).toLowerCase()}` : ''}`
    : null);
  const dintorni = (e) => pilloleDintorni(e.immobile, 4, lingua).map((p) => `${p.breve} ${p.minuti}′`).join(' · ') || null;
  return [
    // un prezzo non plausibile (1 €) non è "il più conveniente": si legge "da verificare" e non si confronta
    riga('prezzo', (e) => formatEuro(e.prezzo, lingua) || (prezzoDaVerificare(e.immobile) ? t('immobili.prezzi.verificare_breve') : null)),
    riga('mq', (e) => formatEuroMq(e.prezzoMq, lingua)),
    riga('superficie', (e) => metriQuadri(e.immobile.superficie), 'metri'),
    riga('locali', (e) => e.immobile.locali || null, 'porta'),
    riga('bagni', (e) => e.immobile.bagni || null, 'bagno'),
    riga('piano', (e) => pianoDato(e.immobile.piano, lingua)?.valore || null, 'scale'),
    riga('classe', (e) => e.immobile.classe_energetica || null, 'foglia'),
    riga('spese', (e) => (e.immobile.spese_mensili ? formatEuro(e.immobile.spese_mensili, lingua) : null), 'euro'),
    riga('agenzia', agenzia),
    riga('parere', (e) => e.immobile.parere_breve || null),
    riga('dintorni', dintorni, 'luogo'),
  ];
};

// ---------------------------------------------------------------- scheda: galleria, visite, storia

// Foto della scheda: la principale più eventuali altre (campo "immagini", se un giorno ci sarà)
export const fotoImmobile = (immobile) => {
  const valida = (u) => typeof u === 'string' && (/^https?:\/\//.test(u) || u.startsWith('/api/files/'));
  const tutte = [immobile.immagine_url, ...(immobile.immagini || [])].filter(valida);
  return [...new Set(tutte)];
};

// La stessa casa pubblicata anche su altri portali (backend/doppioni.py): [{nome, link}] per la riga
// «Anche su idealista.it · casa.it». Senza doppioni e senza il link della scheda.
export const altriPortali = (immobile) => {
  const suo = (immobile?.link || '').replace(/\/+$/, '');
  const visti = new Set([suo]);
  return (immobile?.link_alternativi || [])
    .filter((v) => v && typeof v.link === 'string')
    .filter((v) => {
      const pulito = v.link.replace(/\/+$/, '');
      if (visti.has(pulito)) return false;
      visti.add(pulito);
      return true;
    })
    .map((v) => ({ nome: dominio(v.link) || v.portale || 'altro sito', link: v.link }))
    // due annunci dello stesso portale (la stessa casa pubblicata da due agenzie): il nome non si ripete
    // uguale due volte, il secondo diventa «casa.it (2)»
    .map((v, n, tutti) => {
      const prima = tutti.slice(0, n).filter((x) => x.nome === v.nome).length;
      return prima ? { ...v, nome: `${v.nome} (${prima + 1})` } : v;
    });
};

// Stesse parole della riga della visita sulla card e del calendario (immobili.esito.*)
export const esitoVisita = (visita, lingua = PRINCIPALE) => {
  if (visita.esito_visita === 'positivo') return { classe: 'si', testo: testoEsitoCliente('positivo', lingua) };
  if (visita.esito_visita === 'negativo') {
    const motivi = (visita.motivi || []).map((m) => (ETICHETTE_MOTIVI_VISITA[m] ? contesto(lingua).t(`immobili.motivi_brevi.${m}`) : m).toLowerCase());
    return { classe: 'no',
      testo: motivi.length ? contesto(lingua).t('immobili.esito.negativo_motivi', { motivi: motivi.join(', ') }) : testoEsitoCliente('negativo', lingua) };
  }
  return null;
};

const STATI_STORIA = ['da_contattare', 'non_risponde', 'richiamare', 'da_visionare', 'visita_fissata',
  'visita_richiesta', 'proposta_in_corso', 'rogito_in_corso', 'scartato'];

const nomeBreve = (nome) => (nome || '').split(' ')[0];

// Una voce della "Storia di questa casa": null = non si mostra
export const voceStoria = (evento, immobile, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const meta = evento.metadata || {};
  const delCliente = evento.utente_tipo === 'cliente';
  const chi = delCliente ? t('immobili.storia.voi') : nomeBreve(evento.utente_nome) || t('immobili.storia.noi');
  switch (evento.tipo_evento) {
    case 'immobile_aggiunto': {
      const sito = dominio(immobile?.link) || t('immobili.storia.annuncio');
      return delCliente ? t('immobili.storia.aggiunto_da_voi', { sito }) : t('immobili.storia.proposto_da', { chi, sito });
    }
    case 'dati_compilati': return t(meta.verificati ? 'immobili.storia.dati_verificati' : 'immobili.storia.dati_compilati', { chi });
    case 'dati_estratti': return t('immobili.storia.dati_estratti');
    case 'visita_fissata': {
      const d = leggiData(meta.data_visita);
      if (!d) return t('immobili.storia.visita_fissata');
      const giorno = lingua === PRINCIPALE ? ilGiorno(giornoEMese(d)) : scritta(lingua, d, { day: 'numeric', month: 'long' });
      return t('immobili.storia.visita_fissata_quando', { quando: t('home.riepilogo.alle_ore', { quando: giorno, ora: ora(d) }) });
    }
    // le parole dell'esito della riga della visita, della scheda e del calendario: prima "vi è piaciuta"
    // anche per un trilocale
    case 'visita_effettuata': {
      if (meta.esito === 'positivo') return t('immobili.storia.visita_fatta', { esito: testoEsitoCliente('positivo', lingua) });
      const motivi = (meta.motivi || []).map((m) => (ETICHETTE_MOTIVI_VISITA[m] ? t(`immobili.motivi_brevi.${m}`) : m).toLowerCase());
      const esito = motivi.length ? t('immobili.esito.negativo_motivi', { motivi: motivi.join(', ') }) : testoEsitoCliente('negativo', lingua);
      return t('immobili.storia.visita_fatta', { esito });
    }
    // "Via Rosa del Sasso 8 è sceso a 298.000 €" (testo vecchio del server) o "Il prezzo di via Rosa del Sasso 8 è
    // sceso a …": nella storia della casa basta "Il prezzo è sceso a 298.000 € (era 315.000 €)"
    case 'prezzo_cambiato': {
      const m = String(evento.descrizione || '').match(/ è (sceso|salito) a (.+)$/);
      if (!m) return evento.descrizione || null;
      // il server scrive "298.000 € (era 315.000 €)": anche "era" va nella lingua del cliente
      const prezzo = m[2].replace(/\(era (.+?)\)/, (_, prima) => t('immobili.storia.prezzo_era', { prezzo: prima }));
      return t(m[1] === 'sceso' ? 'immobili.storia.prezzo_sceso' : 'immobili.storia.prezzo_salito', { prezzo });
    }
    case 'visita_richiesta': return t('immobili.storia.visita_richiesta');
    case 'spostamento_richiesto': return t('immobili.storia.spostamento');
    case 'proposta_creata': return (evento.descrizione || '').replace('Proposta creata: offerta', 'Proposta inviata:');
    case 'proposta_aggiornata':
      if (evento.stato_nuovo === 'accettata') return t('immobili.storia.offerta_accettata');
      if (STATI_PROPOSTA_CHIUSI.includes(evento.stato_nuovo)) return t('immobili.storia.proposta_non_accettata');
      // la risposta del venditore registrata nei dati di prima con due voci: resta quella del cambio di stato (qui sopra)
      if (/^Il venditore\b.* ha (accettato|rifiutato) la vostra proposta/.test(evento.descrizione || '')) return null;
      if (meta.testo_cliente) return meta.testo_cliente;
      // modifiche interne dello staff ("Proposta aggiornata: modifica", "…: in_negoziazione"): prima il cliente leggeva
      // quel testo com'era nella storia della casa
      if (/^Proposta aggiornata\b/.test(evento.descrizione || '')) return null;
      return evento.descrizione || null;
    case 'stato_cambiato':
      if (evento.stato_nuovo === 'scartato' && meta.dal_cliente) return t('immobili.storia.non_vi_interessa');
      return STATI_STORIA.includes(evento.stato_nuovo) ? t(`immobili.storia.stati.${evento.stato_nuovo}`) : null;
    case 'immagine_caricata': return null;
    default: return evento.descrizione || null;
  }
};

// Nell'ora di Roma (adesso = adessoRoma): prima l'ora del telefono ("oggi · 05:21" a New York per le 11:21 di Roma,
// "16 settembre" a Tokyo per la sera del 15)
export const storia = (eventi, immobile, adesso, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const quando = (d) => {
    const giorno = quandoGiorno(d, adesso, lingua);
    if (giorno === t('calendario.oggi') || giorno === t('calendario.ieri')) return `${giorno} · ${ora(d)}`;
    return lingua === PRINCIPALE ? conAnno(giornoEMese(d), d, adesso)
      : scritta(lingua, d, { day: 'numeric', month: 'long', ...(d.getFullYear() !== adesso.getFullYear() ? { year: 'numeric' } : {}) });
  };
  return eventi
    .map((e) => ({ evento: e, testo: voceStoria(e, immobile, lingua), data: leggiDataUtcRoma(e.data_evento) }))
    .filter((v) => v.testo && v.data)
    .sort((a, b) => b.data - a.data)
    .map((v) => ({ id: v.evento.id, testo: v.testo, quando: quando(v.data) }));
};

export const propostaAttiva = (proposte, immobileId) => proposte
  .filter((p) => p.immobile_id === immobileId && !STATI_PROPOSTA_CHIUSI.includes(p.stato))
  .find((p) => prezzoNumero({ prezzo_testo: p.prezzo_offerto }));

// La visita fissata nel futuro (non annullata, senza esito), se c'è
export const visitaFutura = (immobile, visite, adesso) => visite.find((v) => v.immobile_id === immobile.id && !v.annullata
  && !v.esito_visita && leggiData(v.data_visita) > adesso) || null;

// Richiesta di visita possibile: non tolta dalla ricerca (fuoriRicerca: anche "Non fa per voi" dopo la visita),
// dati dell'annuncio arrivati (una casa ancora "da compilare" non ha né indirizzo né agenzia), annuncio ancora online,
// niente richiesta già in corso, niente visita futura (le annullate non contano)
export const puoChiedereVisita = (immobile, visite, adesso, proposte = []) => immobile.stato_attivita !== 'scartato'
  && !fuoriRicerca(etichettaCliente(immobile, visite, proposte, adesso))
  && !daCompilare(immobile)
  && !immobile.annuncio_non_online
  && !immobile.visita_richiesta
  && !visitaFutura(immobile, visite, adesso);

// "Non ci interessa più" non si può dire da qui (il server risponde 400): con una visita fissata la disdice lo
// staff con l'agenzia; con una proposta aperta (o accettata) se ne parla con il consulente.
// → 'visita' | 'proposta' | 'accettata' | null
export const bloccoNonInteressa = (immobile, visite, proposte, adesso) => {
  const sue = proposte.filter((p) => p.immobile_id === immobile.id && !STATI_PROPOSTA_CHIUSI.includes(p.stato));
  if (sue.some((p) => p.stato === 'accettata') || immobile.stato_attivita === 'rogito_in_corso') return 'accettata';
  // lo stato "proposta in corso" con le proposte tutte chiuse è "Proposta non accettata" (propostaNonAccettata)
  if (sue.length || (immobile.stato_attivita === 'proposta_in_corso' && !propostaNonAccettata(immobile, proposte))) return 'proposta';
  if (visitaFutura(immobile, visite, adesso) || immobile.prossima_visita) return 'visita';
  return null;
};

export const visiteInSospeso = (visite, adesso) => visite.filter((v) => visitaInSospeso(v, adesso));

// ---------------------------------------------------------------- date della scheda (ora di Roma)

const MESI_BREVI = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];

// "La vostra proposta": le offerte col giorno di Roma (la data si salva in UTC). Prima new Date(o.data) con l'ora del
// telefono: a Tokyo l'offerta della sera risultava del giorno dopo. → [{ ...offerta, giorno: 15, quando: '15 set' }]
export const offerteProposta = (proposta, lingua = PRINCIPALE) => (proposta?.storico_offerte || []).map((o) => {
  const d = leggiDataUtcRoma(o.data);
  const quando = d ? (lingua === PRINCIPALE ? `${d.getDate()} ${MESI_BREVI[d.getMonth()]}` : scritta(lingua, d, { day: 'numeric', month: 'short' })) : '';
  return { ...o, giorno: d ? d.getDate() : null, quando };
});

// L'ultima voce dello storico delle offerte è la controproposta del venditore: la palla è a voi, non al venditore.
// → il prezzo a parole ("375.000 €") o null. Prima home e scheda dicevano "Il venditore risponde entro venerdì 18
// settembre" mentre la campanella diceva "Controproposta del venditore".
export const controproposta = (proposta, lingua = PRINCIPALE) => {
  const storico = proposta?.storico_offerte || [];
  const ultima = storico[storico.length - 1];
  if (ultima?.tipo !== 'controproposta' || !['inviata', 'in_negoziazione'].includes(proposta.stato)) return null;
  return formatEuro(prezzoNumero({ prezzo_testo: ultima.prezzo }), lingua) || String(ultima.prezzo || '').trim() || null;
};
export const testoControproposta = (prezzo, nome, lingua = PRINCIPALE) => contesto(lingua)
  .t('home.controproposta', { prezzo, nome: nome || contesto(lingua).t('comune.il_consulente') });

// La scadenza della proposta è un giorno ("2026-09-18"), letto uguale in qualsiasi fuso
const giornoScadenza = (proposta) => {
  const [a, m, g] = String(proposta?.data_scadenza || '').slice(0, 10).split('-').map(Number);
  return a && m && g ? new Date(a, m - 1, g) : null;
};
// "venerdì 18 settembre"
export const scadenzaProposta = (proposta, adesso, lingua = PRINCIPALE) => {
  const d = giornoScadenza(proposta);
  return d ? dataLunga(d, adesso, lingua) : '';
};

// La scadenza da dire nella scheda, con la stessa regola della home (scadenzaProposta di utils/scheda.js): una
// scadenza già passata non si annuncia al futuro. Prima la scheda diceva "Il venditore risponde entro domenica 13
// settembre" tre giorni dopo, mentre la home taceva. → { passata, quando } | null
// (proposta aperta: con la risposta del venditore la palla è al cliente, e la card lo dice già con controproposta)
export const avvisoScadenza = (proposta, adesso = new Date(), lingua = PRINCIPALE) => {
  if (!['inviata', 'in_negoziazione'].includes(proposta?.stato)) return null;
  const d = giornoScadenza(proposta);
  if (!d) return null;
  const oggi = new Date(adesso.getFullYear(), adesso.getMonth(), adesso.getDate());
  if (d >= oggi) return { passata: false, quando: dataLunga(d, adesso, lingua) };
  // "il 13 settembre" (ilGiorno: "l'8 settembre"), con l'anno se è di un altro anno (conAnno)
  return { passata: true,
    quando: lingua === PRINCIPALE ? conAnno(ilGiorno(giornoEMese(d)), d, adesso) : scritta(lingua, d, { day: 'numeric', month: 'long' }) };
};

// "aggiornato oggi" sotto il parere: il giorno di Roma (adesso = adessoRoma), non quello del telefono
export const quandoParere = (immobile, adesso, lingua = PRINCIPALE) => {
  const d = leggiDataUtcRoma(immobile.parere_data);
  return d ? quandoGiorno(d, adesso, lingua) : '';
};

// ---------------------------------------------------------------- se la comprate

export const TASSO_RISERVA = 4.0;
export const rataMensile = (importo, tasso, anni) => {
  const r = tasso / 100 / 12;
  const n = anni * 12;
  return r ? (importo * r) / (1 - (1 + r) ** -n) : importo / n;
};

// Stima calcolata qui se il server non la dà: mutuo all'80%, 25 anni, tasso di riserva.
// Con un prezzo non plausibile (1 €) niente stima, come il server: non una rata da 0 €
export const stimaLocale = (prezzo, tasso = TASSO_RISERVA) => {
  if (!prezzo || !(prezzo >= PREZZO_MINIMO)) return { disponibile: false };
  const importo = Math.round(prezzo * 0.8);
  return {
    disponibile: true, prezzo: Math.round(prezzo), percentuale: 80, importo_mutuo: importo, anticipo: Math.round(prezzo) - importo,
    anni: 25, tasso, tasso_di_riserva: true, rata: Math.round(rataMensile(importo, tasso, 25)),
  };
};

export { titoloImmobile };
